import React, { FC, ReactNode, useCallback, useEffect } from 'react';
import { Link, LinkProps, useNavigate } from 'react-router-dom';
import styled, { AnyStyledComponent, CSSObject } from 'styled-components';

import Text from './text';
import { CustomColorCheckbox as Checkbox } from '^/components/atoms/CustomColorCheckbox';
import LoadingIcon from '^/components/atoms/LoadingIcon';
import SignInField from '^/components/molecules/SignInField';
import { config } from '^/config';
import dsPalette from '^/constants/ds-palette';
import palette from '^/constants/palette';
import routes from '^/constants/routes';

import MicrosoftButton from '../MicrosoftLoginButton';
import { SignInWithDivider } from './SigninWithDivider';
import SAMLSSOButton from '../SAMLSSOButton';
import { useL10n, UseL10n } from '^/hooks';
import { useSearchParamsQuery } from '^/hooks/useSearchParamsQuery';
import { useAuthStore } from '^/store/auth';
import { useSignInQuery } from '^/store/react-query/auth';
import * as T from '^/types';
import colorTokens from '^/theme/colors/color-tokens';
import Button from '^/components/atoms/Button';
import { FlexBox } from '^/components/atoms/FlexBox';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { l10n } from '^/utilities/l10n';
import { z } from 'zod';

const Root = styled.div({
  boxSizing: 'border-box',
  width: '100%',
  textAlign: 'left',
  margin: '0 auto',
  backgroundColor: palette.white.toString(),
});

const LoginToolsWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '8px',
});

const FindingPasswordLink = styled(Link as AnyStyledComponent).attrs<LinkProps>({
  className: 'no-select',
})({
  color: colorTokens.accentBlue,
  fontSize: '14px',
  textDecoration: 'none',
  fontFamily: 'pretendard, sans-serif',

  ':hover': {
    textDecorationLine: 'underline',
  },
});

const ErrorMessage = styled.p({
  fontSize: '13px',
  lineHeight: 1.3,
  margin: '10px 0',
  color: palette.error.toString(),
});

const SignUpWrapper = styled.div({
  textAlign: 'center',
  margin: 'auto',
  color: '#444',
});

const SignUpLink = styled(Link as AnyStyledComponent).attrs({
  className: 'no-select',
})<LinkProps>({
  marginTop: '20px',
  marginLeft: '5px',
  fontSize: '14px',
  fontWeight: 'bold',
  textDecorationLine: 'none',
  color: colorTokens.accentBlue,
  ':hover': {
    textDecorationLine: 'underline',
  },
});

const CheckboxWrapper = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
});

const CheckboxLabel = styled.span({
  color: palette.textLight.toString(),
  fontSize: '14px',
  minHeight: '16px',
  height: '100%',
  cursor: 'pointer',
  userSelect: 'none',
  lineHeight: '28px',
  fontFamily: 'pretendard, sans-serif',
});

const loadingDivCustomStyle: CSSObject = {
  border: `3px solid ${dsPalette.grey40.toString()}`,
  borderTop: `3px solid ${palette.white.toString()}`,
};

const SingUpText = styled.p({
  color: colorTokens.textGrayLight,
  marginTop: '40px',
  textAlign: 'center',
  fontFamily: 'pretendard, sans-serif',
  fontWeight: '500',
  fontSize: ' 15px',
  lineHeight: '20px',
  letterSpacing: '-0.6%',
  textSlign: 'center',
});

interface LoginInputField {
  email: string;
  password: string;
  autoSignIn: boolean;
}

const PENDING_USER: number = 401;

const SignInForm: FC = () => {
  const [l10nn, language]: UseL10n = useL10n();
  const navigate = useNavigate();

  const emailFromSearch = useSearchParamsQuery().get('email') ?? '';

  const {
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInputField>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(loginValidationSchema(language)),
    defaultValues: { autoSignIn: false, email: emailFromSearch },
  });

  const email = watch('email');
  const isAutoSignIn = watch('autoSignIn');

  const useSignIn = useSignInQuery();
  const { signInStatus, signInError: error, signInErrorCode: errorCode } = useAuthStore();

  const inProgress: boolean = signInStatus === T.APIStatus.PROGRESS;

  const goToPendingRequestPage: () => void = useCallback(() => {
    navigate(`${routes.signup.main}?email=${encodeURIComponent(email)}`);
  }, [email]);

  useEffect(() => {
    if (error !== undefined && errorCode === PENDING_USER) {
      goToPendingRequestPage();
    }
  }, [error]);

  const serverErrorMessage: ReactNode = (() => {
    if (error === undefined) {
      return null;
    }

    switch (error) {
      case T.HTTPError.CLIENT_ERROR:
      case T.HTTPError.CLIENT_AUTH_ERROR:
      case T.HTTPError.CLIENT_NOT_FOUND_ERROR:
      case T.HTTPError.CLIENT_NOT_ACCEPTED_ERROR:
      case T.HTTPError.CLIENT_UNPROCESSABLE_ENTITY_ERROR:
      case T.HTTPError.CLIENT_UNAUTHORIZED_ERROR: {
        return (
          <ErrorMessage data-testid="signin-error-message">
            * {l10nn(Text.error.invalid)}
          </ErrorMessage>
        );
      }
      default: {
        return '';
      }
    }
  })();

  const onSubmitHandler = handleSubmit(async input => {
    useSignIn.mutate(input);
  });

  const loadingIcon: ReactNode = <LoadingIcon loadingDivCustomStyle={loadingDivCustomStyle} />;

  return (
    <>
      <Root>
        <form onSubmit={onSubmitHandler}>
          <ul>
            <SignInField
              kind="email"
              type="text"
              register={register}
              error={errors.email?.message}
            />
            <SignInField
              kind="password"
              type="password"
              register={register}
              error={errors.password?.message}
            />
            {!errors.password?.message && serverErrorMessage}
          </ul>
          <Button
            size="large"
            customStyles={{ width: '100%', marginTop: '20px' }}
            disabled={inProgress}
          >
            {inProgress ? loadingIcon : l10nn(Text.login)}
          </Button>
          <LoginToolsWrapper>
            <CheckboxWrapper>
              <Checkbox
                checked={isAutoSignIn}
                onChange={() => setValue('autoSignIn', !isAutoSignIn)}
              />
              <CheckboxLabel onClick={() => setValue('autoSignIn', !isAutoSignIn)}>
                {l10nn(Text.autoLogin)}
              </CheckboxLabel>
            </CheckboxWrapper>
            <FindingPasswordLink to={routes.password.main}>
              {l10nn(Text.findPassword)}
            </FindingPasswordLink>
          </LoginToolsWrapper>
        </form>
        {config.region === T.Region.DEFAULT && (
          <FlexBox flexDirection="column" justifyContent="center" gap="">
            <SignInWithDivider />
            <MicrosoftButton />
            <SAMLSSOButton />
          </FlexBox>
        )}
        <SingUpText>
          {l10nn(Text.signuplabel)}
          <SignUpLink to={routes.signup.main}>{l10nn(Text.signup)}</SignUpLink>
        </SingUpText>
      </Root>
    </>
  );
};

export function SignUpLinkText() {
  const [l10nn]: UseL10n = useL10n();

  return (
    <SignUpWrapper>
      {l10nn(Text.signuplabel)}
      <SignUpLink to={routes.signup.main}>{l10nn(Text.signup)}</SignUpLink>
    </SignUpWrapper>
  );
}

export default SignInForm;

function loginValidationSchema(language: T.Language) {
  return z.object({
    email: z
      .string()
      .min(1, l10n(Text.error.email.required, language))
      .email(l10n(Text.error.email.pattern, language)),
    password: z.string().nonempty(l10n(Text.error.password.required, language)),
    autoSignIn: z.boolean(),
  });
}
